import * as React from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Section } from '@/components/ui/section'
import { Textarea } from '@/components/ui/textarea'
import { ErrorSummary } from '@/components/visitone/error-summary'
import { SaveBar } from '@/components/visitone/save-bar'
import { useT } from '@/components/visitone/visitone-provider'
import { useConfigurationForm } from '@/hooks/use-configuration-form'
import {
  fieldForKey,
  fieldKey,
  parseKey,
  type FieldDefinition,
  type FormDefinition,
  type FormValues,
} from '@/lib/forms/form-state'

/*
 * Sectioned Form: a long configuration form in independently understandable
 * sections, built on the shared unsaved-changes capability (VIS-321). Changes
 * stay unsaved until Save or Discard; the save bar and each section state how
 * many fields changed. Save stays enabled while invalid, so activating it
 * reveals the error summary and focuses it. Save-on-submit booleans are
 * checkboxes; a Toggle applies on its own and does not belong here.
 *
 * `readOnly` (View only, VIS-329) keeps the sections but shows each value as
 * a label and plain text: no inputs, no save bar, no changed counts, and
 * Cmd/Ctrl+S does nothing, because there is nothing to change.
 */
export function SectionedForm({
  definition,
  initialValues,
  subject,
  onSave,
  readOnly = false,
  initiallyOpen,
}: {
  definition: FormDefinition
  initialValues: FormValues
  /** Names the form in the unsaved-changes dialog. */
  subject: string
  onSave: (values: FormValues) => Promise<void>
  readOnly?: boolean
  /** Section ids open at first; defaults to the first section. */
  initiallyOpen?: string[]
}) {
  const t = useT()
  const form = useConfigurationForm({ definition, initialValues, subject, onSave, readOnly })
  const formId = React.useId()
  const rootRef = React.useRef<HTMLDivElement>(null)
  const summaryRef = React.useRef<HTMLDivElement>(null)
  const [focusTarget, setFocusTarget] = React.useState<string | null>(null)
  const [open, setOpen] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      definition.sections.map((section, index) => [
        section.id,
        initiallyOpen ? initiallyOpen.includes(section.id) : index === 0,
      ]),
    ),
  )

  const domId = (field: Pick<FieldDefinition, 'id'>) => `${formId}-${field.id}`

  const errorKeys = Object.keys(form.errors)
  const invalidBySection: Record<string, number> = {}
  for (const key of errorKeys) {
    const field = fieldForKey(definition, key)
    if (field) invalidBySection[field.section] = (invalidBySection[field.section] ?? 0) + 1
  }

  // After a save attempt with errors: expand those sections and focus the summary.
  const { invalidAttempt } = form
  React.useEffect(() => {
    if (invalidAttempt === 0) return
    const sections = new Set(
      Object.keys(form.errors).map((key) => fieldForKey(definition, key)?.section).filter(Boolean) as string[],
    )
    setOpen((prev) => ({ ...prev, ...Object.fromEntries([...sections].map((id) => [id, true])) }))
    summaryRef.current?.focus()
    // Only a new attempt moves focus; errors changing as fields are fixed must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidAttempt])

  React.useEffect(() => {
    if (!focusTarget) return
    document.getElementById(focusTarget)?.focus()
    setFocusTarget(null)
  }, [focusTarget])

  const jump = (key: string) => {
    const { language } = parseKey(key)
    const field = fieldForKey(definition, key)
    if (!field) return
    if (language && language !== form.language) form.setLanguage(language)
    setOpen((prev) => ({ ...prev, [field.section]: true }))
    setFocusTarget(domId(field))
  }

  const multilingual = form.languages.length > 1
  const fieldLabel = (field: FieldDefinition) =>
    field.translatable && multilingual
      ? t('forms.field.withLanguage', { label: field.label, language: form.languageLabel(form.language) })
      : field.label

  const renderValue = (field: FieldDefinition, label: string) => {
    const value = form.values[fieldKey(field, form.language)]
    const text =
      field.kind === 'checkbox'
        ? t(value ? 'core.on' : 'core.off')
        : field.kind === 'select'
          ? (field.options?.find((option) => option.value === value)?.label ?? String(value ?? ''))
          : String(value ?? '')
    return (
      <div key={field.id} className="flex flex-col gap-1">
        <dt className="text-sm text-fg" style={{ fontWeight: 'var(--vo-label-font-weight)' }}>
          {label}
        </dt>
        <dd className="text-sm text-fg-muted wrap-anywhere whitespace-pre-line">{text}</dd>
      </div>
    )
  }

  const renderField = (field: FieldDefinition) => {
    if (field.visible && !field.visible(form.values)) return null
    const key = fieldKey(field, form.language)
    const value = form.values[key]
    const id = domId(field)

    if (form.readOnly) return renderValue(field, fieldLabel(field))

    if (field.kind === 'checkbox') {
      return (
        <label key={field.id} className="flex items-center gap-1 text-sm text-fg">
          <Checkbox
            id={id}
            checked={Boolean(value)}
            onChange={(event) => form.setValue(key, event.target.checked)}
          />
          {field.label}
        </label>
      )
    }

    const common = {
      value: String(value ?? ''),
      onBlur: () => form.blur(key),
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
        form.setValue(key, event.target.value),
    }
    const label = fieldLabel(field)
    const isDefaultLanguage = form.language === form.languages[0]?.value

    return (
      <Field
        key={field.id}
        id={id}
        label={label}
        help={field.help}
        required={field.required && (!field.translatable || isDefaultLanguage)}
        error={form.errors[key]}
      >
        {field.kind === 'textarea' ? (
          <Textarea {...common} />
        ) : field.kind === 'select' ? (
          <NativeSelect {...common}>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        ) : field.kind === 'number' ? (
          <Input {...common} inputMode={field.inputMode ?? 'numeric'} className="max-w-40" />
        ) : (
          <Input {...common} maxLength={field.maxLength} />
        )}
      </Field>
    )
  }

  return (
    <div ref={rootRef} tabIndex={-1} className="flex max-w-[40rem] flex-col outline-none">
      {form.submitted && (form.summary.length > 0 || form.serverMessages.length > 0) ? (
        <ErrorSummary ref={summaryRef} errors={form.summary} messages={form.serverMessages} onJump={jump} />
      ) : null}

      {multilingual ? (
        <div className="mb-4 max-w-80">
          <Field
            id={`${formId}-content-language`}
            label={t('forms.contentLanguage.label')}
            help={t('forms.contentLanguage.help')}
          >
            <NativeSelect value={form.language} onChange={(event) => form.setLanguage(event.target.value)}>
              {form.languages.map((language) => {
                const count = form.dirtyByLanguage[language.value] ?? 0
                return (
                  <option key={language.value} value={language.value}>
                    {count > 0 ? t('forms.contentLanguage.option', { language: language.label, count }) : language.label}
                  </option>
                )
              })}
            </NativeSelect>
          </Field>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {definition.sections.map((section) => {
          const invalid = invalidBySection[section.id] ?? 0
          const changed = form.dirtyBySection[section.id] ?? 0
          return (
            <Section
              key={section.id}
              headingLevel={2}
              title={section.title}
              summary={section.summary?.(form.baseline)}
              open={open[section.id] ?? false}
              onOpenChange={(next) => setOpen((prev) => ({ ...prev, [section.id]: next }))}
              state={
                invalid > 0
                  ? { tone: 'danger', label: t('forms.section.attention', { count: invalid }) }
                  : changed > 0
                    ? { tone: 'warning', label: t('forms.section.changed', { count: changed }) }
                    : undefined
              }
            >
              {form.readOnly ? (
                <dl className="flex flex-col gap-4">
                  {definition.fields.filter((field) => field.section === section.id).map(renderField)}
                </dl>
              ) : (
                <div className="flex flex-col gap-4">
                  {definition.fields.filter((field) => field.section === section.id).map(renderField)}
                </div>
              )}
            </Section>
          )
        })}
      </div>

      {form.readOnly ? null : (
        <SaveBar
          count={form.dirty.length}
          invalidCount={form.submitted ? form.summary.length + form.serverMessages.length : 0}
          state={form.saveState}
          onDiscard={() => form.requestDiscard(rootRef.current)}
          onSave={() => void form.save()}
        />
      )}
    </div>
  )
}
